import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import DataTable from "./DataTable";
import { Icons } from "../constants/icon-library";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  addCreditToUser,
  deleteUser,
  fetchUsers,
} from "../features/admin/adminSlice";
import { confirmAndDelete } from "./constants";

function Users() {
  const dispatch = useDispatch();
  const formikRef = useRef();

  const { users = [] } = useSelector((state) => state.admin);

  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const data = {
        orgId: selectedUser.orgUuid,
        credits: values.credits,
      };

      await dispatch(addCreditToUser(data)).unwrap();

      await dispatch(fetchUsers());

      document.getElementById("modalCloseButton").click();
      resetForm();
    } catch (error) {
      console.log(error);
    } finally {
      setSubmitting(false);
    }
  };

  const initialCreditValue = {
    credits: "",
  };

  const creditValueValidation = Yup.object().shape({
    credits: Yup.string()
      .trim()
      .matches(/^[0-9\s()+-]+$/, "Contains invalid characters")
      .required("Please enter points to be credited"),
  });

  const handleDeleteUser = async (uuid) => {
    const result = await confirmAndDelete({
      uuid,
      dispatch,
      deleteThunk: deleteUser,
      successMessage: "User deleted successfully.",
      errorMessage: "Failed to delete user.",
    });

    if (result) {
      dispatch(fetchUsers());
    }
  };

  useEffect(() => {
    const modalEl = document.getElementById("addCreditModal");

    if (!modalEl) return;

    const handler = () => {
      if (formikRef.current) {
        formikRef.current.resetForm();
      }
    };

    modalEl.addEventListener("hidden.bs.modal", handler);

    return () => {
      modalEl.removeEventListener("hidden.bs.modal", handler);
    };
  }, []);

  const columns = [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "isVerified", label: "Is Verified" },
    { key: "isDeleted", label: "Deleted" },
    { key: "credits", label: "Credits" },
    { key: "createdAt", label: "Created On" },
    { key: "action", label: "Action", alignEnd: true },
  ];

  const rowRenderer = (c) => (
    <tr key={c.uuid}>
      <td className="align-middle ps-3 py-2">{c.name}</td>
      <td className="align-middle ps-3 py-2">{c.email}</td>

      <td className="align-middle ps-3 py-2">
        {c.isVerified ? (
          <span className="badge bg-success">Verified</span>
        ) : (
          <span className="badge bg-secondary">Unverified</span>
        )}
      </td>

      <td className="align-middle ps-3 py-2">{c.credits}</td>
      <td className="align-middle ps-3 py-2">
        {c.isDeleted ? (
          <span className="badge bg-danger">Deleted</span>
        ) : (
          <span className="badge bg-success">Active</span>
        )}
      </td>
      <td className="align-middle ps-3 py-3">
        {moment.utc(c.createdAt).format("MMM D, YYYY • h:mm A")}
      </td>

      <td className="align-middle text-end">
        <div className="btn-reveal-trigger position-static">
          <button
            className="btn btn-sm dropdown-toggle dropdown-caret-none transition-none btn-reveal fs-10"
            type="button"
            data-bs-toggle="dropdown"
          >
            <i className={`wb ${Icons("dots")} fs-8 me-2 mt-3`} />
          </button>

          <div className="dropdown-menu dropdown-menu-end py-2">
            <button
              className="dropdown-item"
              data-bs-toggle="modal"
              data-bs-target="#addCreditModal"
              onClick={() => setSelectedUser(c)}
            >
              Add Credit
            </button>

            <button
              className="dropdown-item text-danger"
              onClick={() => handleDeleteUser(c.uuid)}
              disabled={c.isDeleted}
            >
              {c.isDeleted ? "Deleted" : "Delete User"}
            </button>
          </div>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="content">
      <div className="row">
        <DataTable
          title="Users"
          data={users}
          columns={columns}
          rowRenderer={rowRenderer}
        />

        {/* Modal */}
        <div
          className="modal fade"
          id="addCreditModal"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content bg-body-highlight p-6">
              <Formik
                innerRef={formikRef}
                initialValues={initialCreditValue}
                validationSchema={creditValueValidation}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting }) => (
                  <Form noValidate>
                    <div className="modal-header border-0 p-0 mb-2">
                      <h3 className="mb-0">Add Credits</h3>
                    </div>

                    <div className="modal-body px-0">
                      <Field
                        name="credits"
                        className="form-control"
                        autoComplete="off"
                      />

                      <ErrorMessage
                        name="credits"
                        component="div"
                        className="invalid-feedback d-block"
                      />
                    </div>

                    <div className="modal-footer border-0 pt-6 px-0 pb-0">
                      <button
                        id="modalCloseButton"
                        className="btn btn-link text-danger"
                        data-bs-dismiss="modal"
                        type="button"
                      >
                        Cancel
                      </button>

                      <button
                        className="btn btn-primary"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Adding..." : "Add Credit"}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Users;
